// Modules
import { Link } from 'react-router';
import { connect } from 'react-redux';
import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { AUTH_SERVICES, CLIENT_AUTH_SERVICES } from 'constants';
import { Card } from 'components/content-containers/cards';
import {
  DeleteButton,
  EditButton,
  RestoreButton,
  SVGButton
} from 'components/widgets/buttons/buttons';
import { MetadataTable, MetadataItem } from 'components/content-containers/tables';
import { dangerIcon, noIcon, yesIcon } from 'components/widgets/badges/badges';
import Address from 'services/data/address';
import Auth from 'services/data/auth';
import CompanyLink from 'components/models/company/company-link';
import MenuOverlay from 'components/widgets/overlays/menu';
import Person from 'services/data/person';
import PhoneNumber from 'services/data/phone-number';
import SVG from 'components/widgets/icons/svg';

function getTitle(title, isBillingContact) {
  if (!isBillingContact) return title;

  return `Can't ${title} billing contacts, remove this person as a billing contact from their company first!`;
}

// Component
function PersonSummary({ person, location, currentUser, update }) {
  const isLead = Person.isLead(currentUser);
  const isPraece = Person.isPraece(person);
  const isUser = Person.isUser(person);
  const isAuth0User = person.auth0Id;
  const roles = person.externalAppAuthRoles;
  const emails = Person.getOrderedContactInfo(person.emailAddresses);
  const numbers = Person.getOrderedContactInfo(person.phoneNumbers);
  const addresses = Person.getOrderedContactInfo(person.addresses);
  const parts = _.compact(_.split(location.pathname, '/'));
  const url = _.join(_.take(parts, parts.length - 2), '/');
  const disabled = Person.isDisabled(person);
  const archived = Person.isArchived(person);
  const isBillingContact = !!_(emails).map('billingCompanies').flatten().value().length;

  return (
    <div className="person-summary flex flex-equal-children">
      <Card className="person-details">
        { (disabled || archived) &&
          <div className="card-header disabled-card-header">
            <div>
              <SVG src="svg/archived.svg" className="no-margin" />
              { disabled && <span className="margin-left">Disabled</span> }
              { archived && <span className="margin-left">Deleted</span> }
            </div>
            <div className="card-header-actions">
              <RestoreButton onClick={() => update({ status: 'Active' })} />
            </div>
          </div>
        }
        <div className="summary-wrapper">
          <div className="card-icon-buttons">
            <Link to={`/${url}/${person.id}/edit`}><EditButton /></Link>
            { !disabled && <SVGButton disabled={isBillingContact} className="archive" src="svg/archived.svg" title={getTitle('Disable', isBillingContact)} onClick={() => update({ status: 'Disabled' })} /> }
            { !archived && <DeleteButton disabled={isBillingContact} title={getTitle('Delete', isBillingContact)} onClick={() => update({ status: 'Archived' })} /> }
          </div>
          <MetadataTable>
            <MetadataItem label="Company">
              <CompanyLink company={person.company} />
            </MetadataItem>
            <MetadataItem label="Security Role">
              {isUser ? yesIcon : noIcon}{person.role}
              { isLead && isAuth0User &&
                <MenuOverlay trigger={<SVG src="svg/cog.svg" />}>
                  <li onClick={() => Auth.resetPassword(person)}>Send Welcome / Reset Password Email</li>
                  <li onClick={() => Auth.reset2FA(person)}>Reset 2-Factor Auth</li>
                </MenuOverlay>
              }
            </MetadataItem>
            { isUser &&
              <MetadataItem label="App Roles">
                { _.map(isPraece ? AUTH_SERVICES : CLIENT_AUTH_SERVICES, service => {
                  const hasRoles = roles[service] && roles[service].length;

                  return (
                    <div key={service} className={hasRoles ? '' : 'deemphasize'}>
                      {hasRoles ? yesIcon : noIcon}{_.startCase(service)}: {hasRoles ? _.join(roles[service], ', ') : 'none'}
                    </div>
                  );
                }) }
              </MetadataItem>
            }

            { isPraece && <MetadataItem label="On Call">{person.onCall ? yesIcon : noIcon }{person.onCall ? 'Yes' : 'No'}</MetadataItem> }
            { isPraece && <MetadataItem label="Notifications">{person.fcmKey ? yesIcon : dangerIcon }{person.fcmKey ? 'Registered' : 'Unregistered'}</MetadataItem> }
            { isPraece && <MetadataItem label="Billing Role">{person.billingRole}</MetadataItem> }
            { (isPraece && person.team) && <MetadataItem label="Team">{person.team.name}</MetadataItem> }
            { person.tags && !!person.tags.length &&
              <MetadataItem label="Tags">
                { _.map(person.tags, (tag, index) =>
                  <span><span key={index} className="tag">{tag}</span> </span>
                ) }
              </MetadataItem>
            }
          </MetadataTable>
        </div>
      </Card>
      <Card className="person-contact-details">
        <MetadataTable>
          <MetadataItem label="Email Addresses">
            { _.map(emails, email => {
              const modifiers = [];

              if (email.primary) modifiers.push('primary');
              if (email.billingCompanies && email.billingCompanies.length) modifiers.push('receives invoices');

              return (
                <p key={email.id}>
                  <span className="deemphasize">
                    {email.type} { !!modifiers.length && <span>({_.join(modifiers, ', ')})</span> }
                  </span><br />
                  <a target="_blank" href={`mailto:${email.email}`}>{email.email}</a>
                </p>
              );
            }) }
          </MetadataItem>
          <MetadataItem label="Phone Numbers">
            { _.map(numbers, number =>
              <p key={number.id}>
                <span className="deemphasize">{number.type}</span><br />
                <a target="_blank" href={`tel:${number.number}`}>{PhoneNumber.format(number.number)}</a>
              </p>
            ) }
          </MetadataItem>
          <MetadataItem label="Addresses">
            { _.map(addresses, address =>
              <p key={address.id}>
                <span className="deemphasize">
                  {address.type} { address.billing && <span>(billing)</span> }
                </span><br />
                <a target="_blank" href={`https://www.google.com/maps/place/${Address.getGoogleMapsPlace(address)}`}>
                  { address.name && <span>{address.name}<br /></span> }
                  {address.address}<br />
                  { address.line2 && <span>{address.line2}<br /></span> }
                  {address.city}, {address.state} {address.zip}
                </a>
              </p>
            ) }
          </MetadataItem>
        </MetadataTable>
      </Card>
    </div>
  );
}

PersonSummary.propTypes = {
  person: PropTypes.object,
  location: PropTypes.object,
  currentUser: PropTypes.object,
  update: PropTypes.func
};

// Exports
export default connect((state) => ({
  person: state.people.person,
  location: state.location,
  currentUser: state.auth.currentUser
}))(PersonSummary);
